/**
 * AI Context Definition for MCP Bridge
 *
 * This defines the "README" that AI agents see when they access
 * window.__GETCLEARANCE__.__AI_CONTEXT__
 *
 * SECURITY: No PII, no secrets, no auth tokens.
 * Only capabilities, selectors, and usage instructions.
 */

export const AI_CONTEXT = {
  app: 'GetClearance - KYC/AML Identity Verification Platform',
  version: '2.0.0',
  docs: 'https://docs.getclearance.dev',

  changelog: {
    '2.0.0': [
      'Action recording with notifications.lastAction/lastError/history',
      'Timeout support on all waitFor methods (default 30s)',
      'waitFor.element(selector) and waitFor.condition(fn)',
      'waitFor.route(path) for navigation waits',
      'Form state exposure via gc.forms (no PII)',
      'Assertion helpers: pageIs, hasData, noErrors, elementExists, formValid',
      '70+ actions across all pages (applicants, cases, workflows, screening, KYB, etc)',
      'Debug mode for troubleshooting (gc.debug.enable())',
      'PII sanitization in all logs',
    ],
  },

  securityNote:
    'This is a compliance platform. PII is NOT exposed via this interface. ' +
    'To access applicant data, use the authenticated API. ' +
    'This interface provides: navigation, actions, IDs, and UI state only. ' +
    'Form values are NEVER exposed - only validation state. ' +
    'Debug logs sanitize emails to [EMAIL] and sensitive data to [REDACTED].',

  quickStart:
    'This is GetClearance, a compliance platform for identity verification.\n' +
    'You can interact via window.__GETCLEARANCE__.actions or by clicking elements with data-testid attributes.\n\n' +
    'Key workflows:\n' +
    '1. Review applicants: Navigate to /applicants, filter by status, click to review\n' +
    '2. Approve/Reject: Use actions.approve(id) or actions.reject(id, reason)\n' +
    '3. Run screening: Use actions.screen(id) to check sanctions/PEP lists\n' +
    '4. Manage cases: Navigate to /cases, view and resolve compliance cases\n' +
    '5. Build workflows: Use createWorkflowFromPrompt() for AI workflow generation\n' +
    '6. KYB: Use createCompany() → screenCompany() → approveCompany() flow\n\n' +
    'ALWAYS: await gc.waitFor.ready() before any interaction\n' +
    'NOTE: Actual applicant data must be fetched via authenticated API.',

  capabilities: [
    // ========================================
    // NAVIGATION
    // ========================================
    { id: 'navigate', description: 'Navigate to any page', action: 'actions.navigate(path)' },

    // ========================================
    // APPLICANTS (10 actions)
    // ========================================
    { id: 'applicants.list', description: 'List and filter applicants', path: '/applicants' },
    { id: 'applicants.review', description: 'Review applicant details', path: '/applicants/:id' },
    { id: 'applicants.approve', description: 'Approve an applicant', action: 'actions.approve(id)' },
    { id: 'applicants.reject', description: 'Reject an applicant', action: 'actions.reject(id, reason)' },
    { id: 'applicants.create', description: 'Create a new applicant', action: 'actions.createApplicant({email, firstName, lastName})' },
    { id: 'applicants.batchApprove', description: 'Bulk approve multiple applicants', action: 'actions.batchApprove([ids])' },
    { id: 'applicants.batchReject', description: 'Bulk reject multiple applicants', action: 'actions.batchReject([ids], reason)' },
    { id: 'applicants.addNote', description: 'Add internal note to applicant', action: 'actions.addApplicantNote(id, content)' },
    { id: 'applicants.requestDocs', description: 'Request documents from applicant', action: 'actions.requestDocuments(id, [types])' },
    { id: 'applicants.reVerify', description: 'Trigger re-verification', action: 'actions.reVerifyApplicant(id)' },
    { id: 'applicants.downloadEvidence', description: 'Download evidence pack PDF', action: 'actions.downloadEvidence(id)' },
    { id: 'applicants.share', description: 'Create reusable KYC share link', action: 'actions.shareApplicant(id)' },
    { id: 'applicants.export', description: 'Export applicants to CSV', action: 'actions.exportApplicants(filters?)' },

    // ========================================
    // CASES (6 actions)
    // ========================================
    { id: 'cases.list', description: 'View compliance cases', path: '/cases' },
    { id: 'cases.create', description: 'Create compliance case', action: 'actions.createCase({applicantId, caseType, description})' },
    { id: 'cases.resolve', description: 'Resolve/close a case', action: 'actions.resolveCase(id, resolution, notes)' },
    { id: 'cases.updateStatus', description: 'Update case status', action: 'actions.updateCaseStatus(id, status)' },
    { id: 'cases.addNote', description: 'Add note to case', action: 'actions.addCaseNote(id, content)' },
    { id: 'cases.escalate', description: 'Escalate case priority', action: 'actions.escalateCase(id, priority, reason)' },
    { id: 'cases.createSAR', description: 'Create Subject Access Request', action: 'actions.createSAR(caseId, data)' },

    // ========================================
    // SCREENING (5 actions)
    // ========================================
    { id: 'screening.run', description: 'Run AML/sanctions screening', action: 'actions.screen(id)' },
    { id: 'screening.runWithTypes', description: 'Run specific check types', action: 'actions.runScreening(applicantId, [checkTypes])' },
    { id: 'screening.runNew', description: 'Screen non-applicant entity', action: 'actions.runNewScreeningCheck({name, country, dob?})' },
    { id: 'screening.resolveHit', description: 'Resolve screening hit', action: 'actions.resolveScreeningHit(hitId, resolution, notes)' },
    { id: 'screening.getAISuggestion', description: 'Get AI recommendation for hit', action: 'actions.getAISuggestion(hitId)' },
    { id: 'screening.refreshSources', description: 'Refresh screening data sources', action: 'actions.refreshScreeningSources()' },

    // ========================================
    // WORKFLOWS (11 actions)
    // ========================================
    { id: 'workflows.list', description: 'List verification workflows', path: '/workflow-builder' },
    { id: 'workflows.create', description: 'Create blank workflow', action: 'actions.createWorkflow({name})' },
    { id: 'workflows.createFromPrompt', description: 'AI generates workflow from natural language', action: 'actions.createWorkflowFromPrompt({name, prompt})' },
    { id: 'workflows.addNode', description: 'Add component node', action: 'actions.addWorkflowNode(workflowId, componentId, position?)' },
    { id: 'workflows.removeNode', description: 'Remove workflow node', action: 'actions.removeWorkflowNode(workflowId, nodeId)' },
    { id: 'workflows.connect', description: 'Connect two nodes', action: 'actions.connectNodes(workflowId, sourceId, targetId, condition?)' },
    { id: 'workflows.chat', description: 'AI assistant modifies workflow', action: 'actions.workflowChat(workflowId, message)' },
    { id: 'workflows.save', description: 'Save workflow changes', action: 'actions.saveWorkflow(workflowId)' },
    { id: 'workflows.publish', description: 'Publish workflow version', action: 'actions.publishWorkflow(workflowId)' },
    { id: 'workflows.rollback', description: 'Rollback to previous version', action: 'actions.rollbackWorkflow(workflowId, versionId)' },
    { id: 'workflows.delete', description: 'Delete workflow', action: 'actions.deleteWorkflow(workflowId)' },
    { id: 'workflows.validate', description: 'Validate workflow for errors', action: 'actions.validateWorkflow(workflowId)' },

    // ========================================
    // COMPANIES / KYB (7 actions)
    // ========================================
    { id: 'companies.list', description: 'List business verifications', path: '/companies' },
    { id: 'companies.create', description: 'Create KYB submission', action: 'actions.createCompany({legalName, country, registrationNumber})' },
    { id: 'companies.screen', description: 'Run business screening', action: 'actions.screenCompany(companyId)' },
    { id: 'companies.approve', description: 'Approve KYB', action: 'actions.approveCompany(companyId)' },
    { id: 'companies.reject', description: 'Reject KYB', action: 'actions.rejectCompany(companyId, reason)' },
    { id: 'companies.addUBODoc', description: 'Add UBO document', action: 'actions.addUBODocument(companyId, uboId, docType)' },
    { id: 'companies.requestDocs', description: 'Request company documents', action: 'actions.requestCompanyDocs(companyId, [types])' },
    { id: 'companies.export', description: 'Export companies to CSV', action: 'actions.exportCompanies(filters?)' },

    // ========================================
    // BILLING (3 actions)
    // ========================================
    { id: 'billing.portal', description: 'Open Stripe billing portal', action: 'actions.openBillingPortal()' },
    { id: 'billing.downloadInvoice', description: 'Download invoice PDF', action: 'actions.downloadInvoice(invoiceId)' },
    { id: 'billing.switchTab', description: 'Switch billing tab', action: 'actions.switchBillingTab(tabName)' },

    // ========================================
    // SETTINGS (3 actions)
    // ========================================
    { id: 'settings.update', description: 'Update settings section', action: 'actions.updateSettings(section, data)' },
    { id: 'settings.reset', description: 'Reset settings to defaults', action: 'actions.resetSettings(section)' },
    { id: 'settings.export', description: 'Export settings config', action: 'actions.exportSettings()' },

    // ========================================
    // AUDIT LOG (3 actions)
    // ========================================
    { id: 'auditLog.filter', description: 'Filter audit log', action: 'actions.filterAuditLog({action?, user?, dateRange?})' },
    { id: 'auditLog.export', description: 'Export audit log CSV', action: 'actions.exportAuditLog(filters?)' },
    { id: 'auditLog.search', description: 'Search audit log', action: 'actions.searchAuditLog(query)' },

    // ========================================
    // QUESTIONNAIRES (5 actions)
    // ========================================
    { id: 'questionnaires.create', description: 'Create questionnaire template', action: 'actions.createQuestionnaire({name, type})' },
    { id: 'questionnaires.edit', description: 'Edit questionnaire', action: 'actions.editQuestionnaire(id, data)' },
    { id: 'questionnaires.delete', description: 'Delete questionnaire', action: 'actions.deleteQuestionnaire(id)' },
    { id: 'questionnaires.preview', description: 'Preview questionnaire', action: 'actions.previewQuestionnaire(id)' },
    { id: 'questionnaires.send', description: 'Send questionnaire to applicant', action: 'actions.sendQuestionnaire(questionnaireId, applicantId)' },

    // ========================================
    // BIOMETRICS (3 actions)
    // ========================================
    { id: 'biometrics.viewReport', description: 'View biometric verification report', action: 'actions.viewBiometricReport(applicantId)' },
    { id: 'biometrics.reRunLiveness', description: 'Request new liveness check', action: 'actions.reRunLiveness(applicantId)' },
    { id: 'biometrics.export', description: 'Export biometric data', action: 'actions.exportBiometricData(applicantId)' },

    // ========================================
    // ANALYTICS (2 actions)
    // ========================================
    { id: 'analytics.setDateRange', description: 'Set analytics date range', action: 'actions.setDateRange(start, end)' },
    { id: 'analytics.export', description: 'Export analytics report', action: 'actions.exportAnalytics(reportType)' },

    // ========================================
    // INTEGRATIONS (4 actions)
    // ========================================
    { id: 'integrations.connect', description: 'Connect integration', action: 'actions.connectIntegration(integrationId)' },
    { id: 'integrations.disconnect', description: 'Disconnect integration', action: 'actions.disconnectIntegration(integrationId)' },
    { id: 'integrations.test', description: 'Test integration connectivity', action: 'actions.testIntegration(integrationId)' },
    { id: 'integrations.webhook', description: 'Configure webhook', action: 'actions.configureWebhook(integrationId, url, [events])' },

    // ========================================
    // REUSABLE KYC (3 actions)
    // ========================================
    { id: 'kycShare.create', description: 'Create KYC share link', action: 'actions.createKYCShare(applicantId, options?)' },
    { id: 'kycShare.revoke', description: 'Revoke share link', action: 'actions.revokeKYCShare(shareId)' },
    { id: 'kycShare.export', description: 'Export share responses', action: 'actions.exportKYCResponses(shareId)' },

    // ========================================
    // UTILITY ACTIONS
    // ========================================
    { id: 'filter', description: 'Filter list by field', action: 'actions.filter(field, value)' },
    { id: 'search', description: 'Search in list', action: 'actions.search(query)' },
    { id: 'select', description: 'Select items in list', action: 'actions.select([ids])' },
    { id: 'refetch', description: 'Refresh query cache', action: 'actions.refetch(queryKey?)' },

    // ========================================
    // DASHBOARD
    // ========================================
    { id: 'dashboard', description: 'View KPIs and activity', path: '/' },
  ],

  selectors: {
    // Core
    appReady: "[data-app-ready='true']",
    pageReady: "[data-page-ready='true']",
    pageLoading: "[data-page-loading='true']",

    // Applicants
    applicantRow: "[data-testid^='applicant-row-']",
    approveButton: "[data-testid='approve-btn']",
    rejectButton: "[data-testid='reject-btn']",
    searchInput: "[data-testid='search-input']",
    statusFilter: "[data-testid='status-filter']",

    // Notifications
    toastContainer: "[data-testid='toast-container']",
    errorToast: "[data-testid*='error']",

    // Workflow Builder
    workflowBuilderPage: "[data-testid='workflow-builder-page']",
    workflowList: "[data-testid='workflow-list']",
    createWorkflowBtn: "[data-testid='create-workflow-btn']",
    workflowItem: "[data-testid^='workflow-item-']",
    workflowCanvas: "[data-testid='workflow-canvas']",
    workflowNode: "[data-testid^='workflow-node-']",
    saveBtn: "[data-testid='save-btn']",
    publishBtn: "[data-testid='publish-btn']",
    previewBtn: "[data-testid='preview-btn']",
    undoBtn: "[data-testid='undo-btn']",
    redoBtn: "[data-testid='redo-btn']",
    chatFab: "[data-testid='chat-fab']",
    chatPanel: "[data-testid='chat-panel']",
    chatInput: "[data-testid='chat-input']",
    chatSendBtn: "[data-testid='chat-send-btn']",

    // Forms
    formValid: "[data-form-valid='true']",
    formInvalid: "[data-form-valid='false']",
    fieldError: "[data-field-valid='false']",

    // Modals
    modal: "[data-testid*='modal']",
    modalClose: "[data-testid='modal-close']",
    confirmBtn: "[data-testid='confirm-btn']",
    cancelBtn: "[data-testid='cancel-btn']",

    // Tables
    tableRow: "[data-testid^='table-row-']",
    checkbox: "[data-testid^='checkbox-']",
    selectAll: "[data-testid='select-all']",

    // Navigation
    sidebar: "[data-testid='sidebar']",
    navItem: "[data-testid^='nav-']",
  },

  tips: [
    'ALWAYS start with: await gc.waitFor.ready()',
    'Use timeout parameter to prevent hangs: await gc.waitFor.ready(10000)',
    'Check for errors after actions: gc.assert.noErrors()',
    'Wait for navigation: await gc.waitFor.route("/applicants")',
    'Wait for elements: await gc.waitFor.element("[data-testid=\\"approve-btn\\"]")',
    'Check form validity before submit: gc.assert.formValid("applicant-form")',
    'View form state (no values): gc.forms["form-id"]',
    'Enable debug for troubleshooting: gc.debug.enable()',
    'View action history: gc.notifications.history',
    'For workflows, use createWorkflowFromPrompt() for AI generation',
    'workflowChat() modifies workflows: "Add liveness check after ID document"',
    'Batch actions for scale testing: batchApprove([id1, id2, ...])',
    'All screening hits can be resolved via resolveScreeningHit()',
    'KYB flow: createCompany() → screenCompany() → approveCompany()',
    'Export logs for debugging: gc.debug.exportLogs()',
    'Action results show in gc.notifications.lastAction',
    'Action errors show in gc.notifications.lastError',
    'Query metadata shows IDs - use these to call actions',
  ],

  waitForExamples: [
    'await gc.waitFor.ready() // Wait for auth',
    'await gc.waitFor.ready(5000) // 5 second timeout',
    'await gc.waitFor.noLoading() // Wait for page to finish loading',
    'await gc.waitFor.querySuccess("applicants") // Wait for query',
    'await gc.waitFor.element("[data-testid=\\"approve-btn\\"]") // Wait for element',
    'await gc.waitFor.route("/applicants") // Wait for navigation',
    'await gc.waitFor.condition(() => gc.queries.applicants?.total > 0) // Custom condition',
  ],

  assertExamples: [
    'gc.assert.pageIs("applicants") // { passed: true, actual: "applicants" }',
    'gc.assert.hasData("applicants") // { passed: true, actual: {...} }',
    'gc.assert.noErrors() // { passed: true, lastError: null }',
    'gc.assert.elementExists("[data-testid=\\"approve-btn\\"]") // { passed: true }',
    'gc.assert.formValid("applicant-form") // { passed: false, message: "..." }',
    'gc.assert.all(() => gc.assert.pageIs("applicants"), () => gc.assert.noErrors())',
  ],

  debugExamples: [
    'gc.debug.enable() // Turn on debug mode',
    'gc.debug.disable() // Turn off for performance',
    'gc.debug.getActionLog() // View all recorded actions',
    'gc.debug.getStateHistory() // View state snapshots',
    'gc.debug.getSummary() // Quick overview',
    'gc.debug.clear() // Reset all logs',
    'gc.debug.exportLogs() // Export for sharing',
  ],
};
